import React, { useContext } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  Modal,
  FormControl,
  FormHelperText,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import CountrySelect from "../../Base/CountrySelect/CountrySelect";
import ownerService from "../../../services/owner.service";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField } from "@mui/x-date-pickers/DateField";
import { STATUS_SUCCESS } from "../../../constants/responseTypes";
import { AlertContext, AlertContextType } from "../../../context/AlertContext";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// i18n.t is typed with incompatible overloads in the installed react-i18next version
const _t = (key: string, opts?: Record<string, unknown>): string =>
  (i18n.t as any)(key, opts);

const initialOwnerObject = {
  firstName: "",
  lastName: "",
  country_code: "",
  phone: "",
  gender: "",
  businessType: "",
  voen: "",
  email: "",
  dateOfBirth: "" as any,
};

const validationSchema = Yup.object({
  firstName: Yup.string()
    .min(2, _t("validation.firstNameMin"))
    .max(40, _t("validation.firstNameMax"))
    .required(_t("validation.firstNameRequired")),
  lastName: Yup.string()
    .min(2, _t("validation.lastNameMin"))
    .max(40, _t("validation.lastNameMax"))
    .required(_t("validation.lastNameRequired")),
  country_code: Yup.string().required(_t("validation.countryCodeRequired")),
  phone: Yup.string()
    .matches(/^[0-9]+$/, _t("validation.phoneNotValid"))
    .min(7, _t("validation.phoneMinDigits"))
    .max(9, _t("validation.phoneMaxDigits"))
    .required(_t("validation.phoneRequired")),
  gender: Yup.string().required(_t("validation.genderRequired")),
  businessType: Yup.string().required(_t("validation.businessTypeRequired")),
  voen: Yup.string().when("businessType", {
    is: (val: string) => val === "Private" || val === "Legal",
    then: (schema) => schema.required(_t("validation.voenRequired")),
    otherwise: (schema) => schema,
  }),
  email: Yup.string().email(_t("validation.invalidEmailFormat")),
  dateOfBirth: Yup.date().nullable().typeError(_t("validation.invalidDateFormat")),
});

type Props = {
  cb?: (data: any) => void;
  isOpen: boolean;
  closeModal: () => void;
  handleGetOwners?: () => Promise<void>;
};

const OwnerForm = ({ cb, isOpen, closeModal, handleGetOwners }: Props) => {
  const { t } = useTranslation();
  const alertContext = useContext<AlertContextType | undefined>(AlertContext);

  const formik = useFormik({
    initialValues: initialOwnerObject,
    validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await ownerService.createOwner(values as any);
        if (response.status === STATUS_SUCCESS) {
          alertContext?.setAlert("success", t("alerts.ownerCreated"));
          if (cb) {
            cb(response.data._id);
          } else {
            closeModal();
          }
          handleGetOwners && (await handleGetOwners());
        }
      } catch (error: any) {
        alertContext?.setAlert("error", error.message || t("alerts.ownerCreateFailed"));
      }
    },
  });

  const err = formik.errors as Record<string, string>;
  const touched = formik.touched as Record<string, boolean>;

  const fieldError = (name: string) =>
    touched[name] && Boolean(err[name]);
  const fieldHelperText = (name: string) =>
    (touched[name] && err[name]) as string | undefined;

  return (
    <Box>
      <Modal
        open={isOpen}
        onClose={closeModal}
        className="flex justify-center items-center"
      >
        <Box
          className="bg-white p-5 rounded-lg"
          sx={{ width: { xs: "92vw", sm: "80%", md: "66%", lg: "50%", xl: "38%" }, maxHeight: "90vh", overflowY: "auto" }}
        >
          <Typography variant="h4" mb={2}>{t("forms.owner.title")}</Typography>
          <form onSubmit={formik.handleSubmit}>
            {/* Uniform vertical rhythm for every field row. */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>

            {/* First Name / Last Name */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
              <TextField
                fullWidth
                id="firstName"
                name="firstName"
                label={t("forms.owner.firstName")}
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("firstName")}
                helperText={fieldHelperText("firstName")}
              />
              <TextField
                fullWidth
                id="lastName"
                name="lastName"
                label={t("forms.owner.lastName")}
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("lastName")}
                helperText={fieldHelperText("lastName")}
              />
            </Box>

            {/* Phone */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: 2,
              }}
            >
              <Box sx={{ width: { xs: "100%", sm: "180px" }, flexShrink: 0 }}>
                <CountrySelect
                  formik={formik}
                  label="country_code"
                  error={fieldError("country_code")}
                  sx={{ width: "100%" }}
                />
                {fieldError("country_code") && (
                  <FormHelperText error sx={{ mx: "14px" }}>
                    {fieldHelperText("country_code")}
                  </FormHelperText>
                )}
              </Box>
              <TextField
                fullWidth
                id="phone"
                name="phone"
                label={t("forms.owner.phone")}
                inputMode="numeric"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("phone")}
                helperText={fieldHelperText("phone")}
              />
            </Box>

            {/* Business Type / Gender */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <FormControl fullWidth error={fieldError("businessType")}>
                <InputLabel id="business-type-label">
                  {t("forms.owner.businessType")}
                </InputLabel>
                <Select
                  labelId="business-type-label"
                  id="business-type-select"
                  name="businessType"
                  value={formik.values.businessType}
                  label={t("forms.owner.businessType")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="Citizen">{t("forms.owner.businessTypeCitizen")}</MenuItem>
                  <MenuItem value="Private">{t("forms.owner.businessTypePrivate")}</MenuItem>
                  <MenuItem value="Legal">{t("forms.owner.businessTypeLegal")}</MenuItem>
                </Select>
                {fieldError("businessType") && (
                  <FormHelperText>{fieldHelperText("businessType")}</FormHelperText>
                )}
              </FormControl>

              <FormControl fullWidth error={fieldError("gender")}>
                <InputLabel id="gender-label">{t("forms.owner.gender")}</InputLabel>
                <Select
                  labelId="gender-label"
                  id="gender-select"
                  name="gender"
                  value={formik.values.gender}
                  label={t("forms.owner.gender")}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="Male">{t("forms.owner.genderMale")}</MenuItem>
                  <MenuItem value="Female">{t("forms.owner.genderFemale")}</MenuItem>
                  <MenuItem value="Other">{t("forms.owner.genderOther")}</MenuItem>
                </Select>
                {fieldError("gender") && (
                  <FormHelperText>{fieldHelperText("gender")}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* VOEN (conditional) */}
            {(formik.values.businessType === "Private" ||
              formik.values.businessType === "Legal") && (
              <TextField
                fullWidth
                id="voen"
                name="voen"
                label={t("forms.owner.voen")}
                value={formik.values.voen}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("voen")}
                helperText={fieldHelperText("voen")}
              />
            )}

            {/* Date of Birth / Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: { xs: "stretch", sm: "flex-start" },
              }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateField
                  label={t("forms.owner.dateOfBirth")}
                  name="dateOfBirth"
                  sx={{ width: "100%" }}
                  onChange={(val: any) =>
                    formik.setFieldValue("dateOfBirth", val ? val.$d : null)
                  }
                />
              </LocalizationProvider>

              <TextField
                fullWidth
                id="email"
                name="email"
                label={t("forms.owner.email")}
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={fieldError("email")}
                helperText={fieldHelperText("email")}
              />
            </Box>

            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
              >
                {t("forms.owner.submit")}
              </Button>
              <Button
                color="secondary"
                variant="outlined"
                fullWidth
                type="reset"
                onClick={closeModal}
              >
                {t("common.cancel")}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>
    </Box>
  );
};

export default OwnerForm;
